import React from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTint } from '@fortawesome/free-solid-svg-icons';
import { getPediatricBMICutoffs } from '../../utils/BMIReference';

type Props = {
  bmi: number;
  ageYears?: number;
  sex?: 'M' | 'F';
  width?: number;
};

const TINT_PATH = faTint.icon[4] as string;
const TINT_VIEWBOX = `0 0 ${faTint.icon[0]} ${faTint.icon[1]}`;

const GREEN     = '#78F880';
const YELLOW    = '#FEFA83';
const ORANGE    = '#FFD37E';
const RED       = '#FC6552';
const DARK_RED  = '#B22222';

const CHART_MIN = 12;
const CHART_MAX = 45;

type Band = { from: number; to: number; color: string; label: string; fullLabel: string };

function adultBands(): { bands: Band[]; ticks: number[] } {
  return {
    bands: [
      { from: CHART_MIN, to: 18.5,       color: YELLOW,   label: 'Under',    fullLabel: 'Underweight' },
      { from: 18.5,      to: 25,         color: GREEN,    label: 'Normal',   fullLabel: 'Normal' },
      { from: 25,        to: 30,         color: YELLOW,   label: 'Over',     fullLabel: 'Overweight' },
      { from: 30,        to: 35,         color: ORANGE,   label: 'Obese I',  fullLabel: 'Obese Class I' },
      { from: 35,        to: 40,         color: RED,      label: 'Obese II', fullLabel: 'Obese Class II' },
      { from: 40,        to: CHART_MAX,  color: DARK_RED, label: 'Obese III',fullLabel: 'Obese Class III' },
    ],
    ticks: [CHART_MIN, 18.5, 25, 30, 35, 40, CHART_MAX],
  };
}

function pediatricBands(p5: number, p85: number, p95: number): { bands: Band[]; ticks: number[] } {
  return {
    bands: [
      { from: CHART_MIN, to: p5,        color: YELLOW, label: 'Under',   fullLabel: 'Underweight (<5th %ile)' },
      { from: p5,        to: p85,       color: GREEN,  label: 'Healthy', fullLabel: 'Healthy (5th–85th %ile)' },
      { from: p85,       to: p95,       color: ORANGE, label: 'Over',    fullLabel: 'Overweight (85th–95th %ile)' },
      { from: p95,       to: CHART_MAX, color: RED,    label: 'Obese',   fullLabel: 'Obese (≥95th %ile)' },
    ],
    ticks: [CHART_MIN, p5, p85, p95, CHART_MAX],
  };
}

function findBand(bmi: number, bands: Band[]): Band {
  const clamped = Math.max(CHART_MIN, Math.min(CHART_MAX, bmi));
  for (const b of bands) {
    if (clamped >= b.from && clamped < b.to) return b;
  }
  return bands[bands.length - 1];
}

function textColorOn(bandColor: string): string {
  return (bandColor === RED || bandColor === DARK_RED) ? '#fff' : '#212529';
}

export const BMIBarChart: React.FC<Props> = ({ bmi, ageYears, sex, width }) => {
  if (bmi == null || isNaN(bmi)) return null;
  if (ageYears != null && ageYears < 2) return null;

  const pediatric = (ageYears != null && ageYears >= 2 && ageYears < 20 && sex)
    ? getPediatricBMICutoffs(ageYears, sex)
    : null;

  const { bands, ticks } = pediatric
    ? pediatricBands(pediatric.underweight, pediatric.overweight, pediatric.obese)
    : adultBands();

  const clampedBmi = Math.max(CHART_MIN, Math.min(CHART_MAX, bmi));
  const currentBand = findBand(bmi, bands);

  const options: Highcharts.Options = {
    chart: {
      type: 'bar',
      height: 36,
      marginTop: 1,
      marginBottom: 19,
      marginLeft: 0,
      marginRight: 0,
      animation: false,
      backgroundColor: 'transparent',
    },
    title: { text: '' },
    xAxis: [{ labels: { enabled: false }, tickLength: 0, lineWidth: 0 }],
    yAxis: {
      title: { text: '' },
      labels: {
        enabled: true,
        formatter(this: any) {
          const v: number = this.value;
          if (v === CHART_MIN || v === CHART_MAX) return '';
          return String(v);
        },
        style: { fontSize: '10px', color: '#555' },
      },
      min: CHART_MIN,
      max: CHART_MAX,
      tickPositions: ticks,
      gridLineWidth: 0,
      lineWidth: 0,
      plotBands: bands.map(b => ({ from: b.from, to: b.to, color: b.color })),
    },
    series: [{
      type: 'bar',
      data: [clampedBmi],
      color: 'rgba(0,0,0,0)',
      borderWidth: 0,
      enableMouseTracking: false,
      pointWidth: 1,
      dataLabels: { enabled: false },
    } as Highcharts.SeriesBarOptions],
    tooltip: { enabled: false },
    legend: { enabled: false },
    credits: { enabled: false },
    exporting: { enabled: false },
  };

  const range = CHART_MAX - CHART_MIN;

  const rangeInfoTooltip = (
    <Tooltip id="bmi-range-info-tooltip">
      <div style={{ textAlign: 'left' }}>
        BMI is interpreted using two different reference systems depending on age:
        <br /><br />
        <strong>Ages 2–19</strong> — pediatric CDC percentile cutoffs for the client's age and sex (underweight &lt;5th, healthy 5th–85th, overweight 85th–95th, obese ≥95th) — PMID: 12043359.
        <br /><br />
        <strong>Ages 20+</strong> — standard adult WHO/CDC fixed cutoffs (underweight &lt;18.5, normal 18.5–25, overweight 25–30, obese ≥30) — PMID: 11234459.
        <br /><br />
        <strong>Under 2</strong> — BMI is not shown; CDC uses weight-for-length, not BMI, in this age range.
      </div>
    </Tooltip>
  );

  return (
    <div style={{
      width: width != null ? `${width}px` : 450,
      border: '1px solid #dee2e6',
      borderRadius: 4,
      padding: '8px 10px',
      backgroundColor: '#fff',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
    }}>
      <div style={{
        flex: '0 0 auto',
        textAlign: 'center',
        lineHeight: 1.1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignSelf: 'stretch',
      }}>
        <div style={{
          fontSize: 12,
          fontWeight: 700,
          color: '#666',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
        }}>BMI</div>
        <div style={{
          fontSize: 22,
          fontWeight: 700,
          color: '#212529',
          marginTop: 2,
        }}>{bmi}</div>
        <div
          title={currentBand.fullLabel}
          style={{
            fontSize: 9,
            fontWeight: 700,
            backgroundColor: currentBand.color,
            color: textColorOn(currentBand.color),
            borderRadius: 2,
            padding: '3px 5px 2px',
            marginTop: 3,
            display: 'inline-block',
            whiteSpace: 'nowrap',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}
        >
          {currentBand.label}
        </div>
      </div>
      <div style={{ width: 1, alignSelf: 'stretch', backgroundColor: '#dee2e6' }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ position: 'relative', height: 12, marginBottom: 1 }}>
          {bands.map((b, i) => {
            const leftPct = ((b.from - CHART_MIN) / range) * 100;
            const widthPct = ((b.to - b.from) / range) * 100;
            return (
              <div
                key={i}
                title={b.fullLabel}
                style={{
                  position: 'absolute',
                  left: `${leftPct}%`,
                  width: `${widthPct}%`,
                  textAlign: 'center',
                  fontSize: '11px',
                  lineHeight: '12px',
                  color: '#333',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {b.label}
              </div>
            );
          })}
        </div>
        <div style={{ position: 'relative' }}>
          <HighchartsReact options={options} highcharts={Highcharts} />
          <div
            style={{
              position: 'absolute',
              left: `calc(${((clampedBmi - CHART_MIN) / range) * 100}% - 7px)`,
              top: 2,
              width: 14,
              height: 14,
              pointerEvents: 'none',
              lineHeight: 0,
            }}
          >
            <svg viewBox={TINT_VIEWBOX} width={14} height={14} xmlns="http://www.w3.org/2000/svg">
              <path d={TINT_PATH} fill="#111" />
            </svg>
          </div>
        </div>
      </div>
      <OverlayTrigger
        overlay={rangeInfoTooltip}
        placement="right"
        flip={false}
        popperConfig={{ modifiers: [{ name: 'flip', enabled: false }] }}
      >
        <FontAwesomeIcon
          icon="info-circle"
          style={{ color: '#888', cursor: 'pointer', flex: '0 0 auto' }}
        />
      </OverlayTrigger>
    </div>
  );
};
